import PrimusvestLogo from '../icons/primusvest.png'
import { getBankTransferDetailsFromOrder } from './bankTransfer'
import { shortenOrderNumber } from './orderNumber'

const formatCurrency = (value) => new Intl.NumberFormat('ro-RO', {
  style: 'currency',
  currency: 'RON',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
}).format(Number(value || 0))

const DEFAULT_ISSUER = {
  company: 'PRIMUSVEST SRL',
  cui: 'RO35750706',
  registrationNo: 'J05/485/2016',
  address: 'Sat Sarbesti nr. 72, cod postal 417323, jud. Bihor, Romania',
  workPointAddress: 'Str. Seleusului 117, cod postal 410508, Oradea, jud. Bihor, Romania',
  email: 'office@primusvest.ro',
  phone: '+40 770 585 736',
  website: 'www.primusvest.ro'
}

const buildIssuer = (order) => {
  const seller = order?.seller_snapshot || {}
  return {
    company: seller.company || DEFAULT_ISSUER.company,
    cui: seller.cui || DEFAULT_ISSUER.cui,
    registrationNo: seller.registration_no || seller.registrationNo || DEFAULT_ISSUER.registrationNo,
    address: seller.address || DEFAULT_ISSUER.address,
    workPointAddress: seller.work_point_address || seller.workPointAddress || DEFAULT_ISSUER.workPointAddress,
    email: seller.email || DEFAULT_ISSUER.email,
    phone: seller.phone || DEFAULT_ISSUER.phone,
    website: seller.website || DEFAULT_ISSUER.website
  }
}

const buildIssueDate = (order) => (
  order?.created_at
    ? new Date(order.created_at).toLocaleString('ro-RO')
    : new Date().toLocaleString('ro-RO')
)

const buildAddress = (value) => String(value || '-').replace(/\s+/g, ' ').trim() || '-'

const PDF_CHAR_REPLACEMENTS = {
  ă: 'a',
  â: 'a',
  î: 'i',
  ș: 's',
  ş: 's',
  ț: 't',
  ţ: 't',
  Ă: 'A',
  Â: 'A',
  Î: 'I',
  Ș: 'S',
  Ş: 'S',
  Ț: 'T',
  Ţ: 'T'
}

const toPdfSafeText = (value) => buildAddress(value)
  .replace(/[ăâîșşțţĂÂÎȘŞȚŢ]/g, (character) => PDF_CHAR_REPLACEMENTS[character] || character)
  .replace(/,\s*/g, ', ')
  .replace(/Nr\.(?=\S)/g, 'Nr. ')

const formatPdfRowValue = (label, value) => {
  const normalized = toPdfSafeText(value)
  if (normalized === '-') {
    return normalized
  }

  if (label === 'Adresa facturare' || label === 'Adresa livrare' || label === 'Sediu') {
    return normalized.replace(/,\s+/g, ',\n')
  }

  return normalized
}

const loadImageAsDataUrl = async (src) => {
  const response = await fetch(src)
  const blob = await response.blob()

  return await new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

const addWrappedText = (doc, text, x, y, maxWidth, options = {}) => {
  const normalizedText = String(text || '-')
  const paragraphs = normalizedText.split('\n')
  const lines = paragraphs.flatMap((paragraph) => doc.splitTextToSize(toPdfSafeText(paragraph), maxWidth))
  doc.text(lines, x, y, options)
  return y + (lines.length * 5)
}

const drawMetaRow = (doc, {
  label,
  value,
  x,
  y,
  width
}) => {
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9.5)
  doc.text(label, x, y)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10.5)
  const lines = doc.splitTextToSize(toPdfSafeText(value), width)
  doc.text(lines, x, y + 5)
  return y + 7 + (lines.length * 4.5)
}

const buildItemsRows = (order) => {
  const items = Array.isArray(order?.items) ? order.items : []
  return items.map((item, index) => ([
    String(index + 1),
    toPdfSafeText(item.name),
    String(item.quantity || 0),
    toPdfSafeText(item.stock_label || ''),
    formatCurrency(item.effective_price),
    formatCurrency(item.total_price)
  ]))
}

export const hasIssuedInvoice = (order) => Boolean(
  order?.stripe_invoice_pdf || order?.stripe_invoice_url || order?.stripe_receipt_url
)

const buildDisplayNumber = (document, kind) => {
  if (document?.order_number) {
    return shortenOrderNumber(document.order_number)
  }
  return document?.offer_number || `${kind}-${new Date().toISOString().slice(0, 10)}`
}

const drawSectionCard = (doc, {
  title,
  rows,
  x,
  y,
  width,
  minHeight = 0
}) => {
  const paddingX = 5
  const titleY = y + 9
  const valueWidth = width - (paddingX * 2)

  doc.setDrawColor(221, 221, 221)

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.text(title, x + paddingX, titleY)

  let cursorY = titleY + 5
  rows.forEach(({ label, value }) => {
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(9.5)
    doc.text(label, x + paddingX, cursorY)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(10.5)
    cursorY = addWrappedText(doc, formatPdfRowValue(label, value), x + paddingX, cursorY + 5, valueWidth) + 1.5
  })

  const finalHeight = Math.max(minHeight, cursorY - y + 4)
  doc.roundedRect(x, y, width, finalHeight, 4, 4, 'S')
  return finalHeight
}

const downloadCommercialDocument = async (documentData, options) => {
  if (!documentData) {
    return
  }

  const [{ jsPDF: JsPdf }, { default: autoTable }] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable')
  ])

  const logoDataUrl = await loadImageAsDataUrl(PrimusvestLogo).catch(() => null)
  const doc = new JsPdf({
    unit: 'mm',
    format: 'a4'
  })

  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const marginX = 14
  const contentWidth = pageWidth - (marginX * 2)
  const cardGap = 6
  const halfWidth = (contentWidth - cardGap) / 2
  const issuer = buildIssuer(documentData)
  const {
    title,
    subtitle,
    disclaimer,
    fileName,
    issuerTitle = 'Emis de',
    metaNumberLabel = 'Numar comanda',
    metaDateLabel = 'Data emiterii'
  } = options

  doc.setFillColor(248, 248, 248)
  doc.setDrawColor(230, 230, 230)
  doc.roundedRect(marginX, 12, contentWidth, 30, 5, 5, 'FD')

  const logoWidth = 28
  const logoHeight = 10
  const logoX = pageWidth - marginX - 6 - logoWidth
  const logoY = 17

  if (logoDataUrl) {
    doc.addImage(logoDataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight)
  }
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(22)
  doc.text(title, marginX + 6, 22)

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10.5)
  doc.text(subtitle, marginX + 6, 29)
  doc.text(disclaimer, marginX + 6, 34)

  doc.setFillColor(255, 255, 255)
  doc.setDrawColor(230, 230, 230)
  doc.roundedRect(marginX, 48, contentWidth, 22, 4, 4, 'FD')
  const metaLeftX = marginX + 6
  const metaRightX = marginX + (contentWidth / 2) + 3
  drawMetaRow(doc, {
    label: metaNumberLabel,
    value: buildDisplayNumber(documentData, title.toUpperCase()),
    x: metaLeftX,
    y: 56,
    width: (contentWidth / 2) - 12
  })
  drawMetaRow(doc, {
    label: metaDateLabel,
    value: buildIssueDate(documentData),
    x: metaRightX,
    y: 56,
    width: (contentWidth / 2) - 12
  })

  const issuerRows = [
    {
      label: 'Furnizor',
      value: issuer.company
    },
    {
      label: 'Identificare',
      value: `CUI ${issuer.cui} | Nr. Reg. Com. ${issuer.registrationNo}`
    },
    {
      label: 'Sediu',
      value: issuer.address
    },
    {
      label: 'Punct de lucru',
      value: issuer.workPointAddress
    },
    {
      label: 'Contact',
      value: `${issuer.email} | ${issuer.phone} | ${issuer.website}`
    }
  ]

  const issuerHeight = drawSectionCard(doc, {
    title: issuerTitle,
    rows: issuerRows,
    x: marginX,
    y: 78,
    width: contentWidth,
    minHeight: 30
  })

  const billingRows = [
    {
      label: 'Companie',
      value: documentData?.company_snapshot?.company || documentData?.billing_snapshot?.company || '-'
    },
    {
      label: 'CUI',
      value: documentData?.company_snapshot?.cui || '-'
    },
    {
      label: 'Nr. Reg. Com.',
      value: documentData?.company_snapshot?.company_profile?.registration_no || documentData?.billing_snapshot?.registration_no || '-'
    },
    {
      label: 'Adresa facturare',
      value: documentData?.billing_snapshot?.full_address || '-'
    }
  ]

  const deliveryRows = [
    {
      label: 'Adresa livrare',
      value: documentData?.delivery_snapshot?.full_address || documentData?.billing_snapshot?.full_address || '-'
    },
    {
      label: 'Destinatar',
      value: documentData?.delivery_snapshot?.recipient_name || documentData?.customer_snapshot?.username || '-'
    },
    {
      label: 'Telefon',
      value: documentData?.delivery_snapshot?.recipient_phone || documentData?.customer_snapshot?.phone || '-'
    },
    {
      label: 'Companie / punct de lucru',
      value: documentData?.delivery_snapshot?.company || '-'
    }
  ]

  const cardsY = 78 + issuerHeight + 8
  const billingHeight = drawSectionCard(doc, {
    title: 'Facturare',
    rows: billingRows,
    x: marginX,
    y: cardsY,
    width: halfWidth,
    minHeight: 48
  })

  const deliveryHeight = drawSectionCard(doc, {
    title: 'Livrare',
    rows: deliveryRows,
    x: marginX + halfWidth + cardGap,
    y: cardsY,
    width: halfWidth,
    minHeight: 48
  })

  const ensurePageSpace = (cursorY, requiredHeight) => {
    if (cursorY + requiredHeight > pageHeight - 14) {
      doc.addPage()
      return 22
    }
    return cursorY
  }

  let nextY = cardsY + Math.max(billingHeight, deliveryHeight) + 10
  const bankTransferDetails = getBankTransferDetailsFromOrder(documentData)
  if (documentData?.payment_method === 'bank_transfer' && bankTransferDetails.configured) {
    nextY = ensurePageSpace(nextY, 58)
    const bankTransferHeight = drawSectionCard(doc, {
      title: 'Detalii plata prin ordin de plata',
      rows: [
        { label: 'Beneficiar', value: bankTransferDetails.beneficiary },
        { label: 'IBAN', value: bankTransferDetails.iban },
        { label: 'Banca', value: bankTransferDetails.bank_name },
        { label: bankTransferDetails.reference_label, value: shortenOrderNumber(bankTransferDetails.reference_value || buildDisplayNumber(documentData, 'PROFORMA')) }
      ],
      x: marginX,
      y: nextY,
      width: contentWidth,
      minHeight: 28
    })
    nextY += bankTransferHeight + 8
  }

  nextY = ensurePageSpace(nextY, 38)

  autoTable(doc, {
    startY: nextY,
    head: [['#', 'Produs', 'Cant.', 'Disponibilitate', 'Pret unitar', 'Total']],
    body: buildItemsRows(documentData),
    margin: { left: marginX, right: marginX },
    styles: {
      fontSize: 9,
      cellPadding: 2.5,
      valign: 'middle'
    },
    headStyles: {
      fillColor: [245, 245, 245],
      textColor: [60, 60, 60]
    },
    bodyStyles: {
      textColor: [35, 35, 35]
    },
    columnStyles: {
      0: { cellWidth: 8, halign: 'center' },
      1: { cellWidth: 70 },
      2: { cellWidth: 14, halign: 'center' },
      3: { cellWidth: 28 },
      4: { cellWidth: 28, halign: 'right' },
      5: { cellWidth: 28, halign: 'right' }
    },
    theme: 'grid'
  })

  const finalY = doc.lastAutoTable?.finalY || nextY
  let totalsY = finalY + 8
  const totalsBoxWidth = 74
  const totalsBoxHeight = 30
  const totalsBoxX = pageWidth - marginX - totalsBoxWidth

  if (totalsY + totalsBoxHeight > pageHeight - 14) {
    doc.addPage()
    totalsY = 22
  }

  doc.setDrawColor(221, 221, 221)
  doc.setFillColor(255, 255, 255)
  doc.roundedRect(totalsBoxX, totalsY - 4, totalsBoxWidth, totalsBoxHeight, 4, 4, 'FD')
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.text('Subtotal produse', totalsBoxX + 4, totalsY + 2)
  doc.text(formatCurrency(documentData?.subtotal_price), pageWidth - marginX - 4, totalsY + 2, { align: 'right' })
  doc.text('Transport', totalsBoxX + 4, totalsY + 9)
  doc.text(formatCurrency(documentData?.shipping_price), pageWidth - marginX - 4, totalsY + 9, { align: 'right' })
  doc.setDrawColor(226, 6, 19)
  doc.line(totalsBoxX + 4, totalsY + 13, pageWidth - marginX - 4, totalsY + 13)
  doc.setTextColor(226, 6, 19)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(13)
  doc.text('Total', totalsBoxX + 4, totalsY + 21)
  doc.text(formatCurrency(documentData?.total_price), pageWidth - marginX - 4, totalsY + 21, { align: 'right' })
  doc.setTextColor(0, 0, 0)

  doc.save(fileName)
}

export const downloadOrderProforma = async (order) => {
  if (!order) {
    return
  }

  await downloadCommercialDocument(order, {
    title: 'Proforma',
    subtitle: 'Document comercial preliminar',
    disclaimer: 'Acest document nu reprezinta factura fiscala.',
    fileName: `${buildDisplayNumber(order, 'PROFORMA')}.pdf`,
    issuerTitle: 'Emis de',
    metaNumberLabel: 'Numar comanda',
    metaDateLabel: 'Data emiterii'
  })
}

export const downloadCartOfferPdf = async (offer) => {
  if (!offer) {
    return
  }

  await downloadCommercialDocument(offer, {
    title: 'Oferta',
    subtitle: 'Oferta comerciala estimativa',
    disclaimer: 'Preturile si disponibilitatea sunt calculate pe baza cosului curent.',
    fileName: `${buildDisplayNumber(offer, 'OFERTA')}.pdf`,
    issuerTitle: 'Propusa de',
    metaNumberLabel: 'Referinta oferta',
    metaDateLabel: 'Data generarii'
  })
}
